import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { HistoryIcon, XIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Input from '@repo/ui/input'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import { getAllCseReductionRules } from '../../../../api/cse/cse-reduction-rules'
import { getCseByCode } from '../../../../api/cse/get-cse-by-code'
import { addCseToEnrollment } from '../../../../api/enrollments/add-cse'
import { useObserver } from '../../../../lib/contexts/observer'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import { Enrollment } from '../../../../lib/types/models/enrollment/enrollment'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'

type Props = {
  enrollment: Pick<Enrollment, 'id'> & { cseRegistration?: { cse: { code?: string } } }
}

export function EnrollmentCseHeader({ enrollment }: Props) {
  const defaultCseCode = enrollment?.cseRegistration?.cse?.code
  const { notify } = useObserver()
  const [cseCode, setCseCode] = useState<string>()
  const [isLoading, setIsLoading] = useState(false)
  const { selectedData, modalState, toggleModal, closeModel } = useModalManager<'add-cse', string>(
    ['add-cse']
  )

  const { mutateAsync: addCseMutation } = useMutation({
    mutationFn: addCseToEnrollment,
    onError: showAxiosError,
    onSuccess: async () => {
      closeModel('add-cse')

      await notify([
        'enrollment-profile',
        'enrollment-status',
        'enrollment-form-update',
        'enrollment-payment-record',
        'enrollment-profile-monitoring-payment-record',
        'enrollment-overpayment',
        'enrollment-proposition-summary'
      ])

      toast.success('Le code a été ajouté avec succès')
    }
  })

  async function addCseCode(code: string) {
    setIsLoading(true)
    const normalizedCseCode = code?.trim()
    if (!normalizedCseCode) return

    try {
      const cse = await getCseByCode(normalizedCseCode, 'BASIC')

      if (!cse.isActive) {
        setCseCode(undefined)
        toast.error("Le CSE n'est pas actif")

        return
      }

      const cseReductions = await getAllCseReductionRules(cse.id, { count: true })

      if (cseReductions.total === 0) {
        setCseCode(undefined)
        toast.error("Le CSE n'a pas de règles de réduction")
        return
      }

      addCseMutation({ cseCode: normalizedCseCode, enrollmentId: enrollment.id })
    } catch {
      setCseCode(undefined)
      toast.error('Le code CSE saisi est invalide')
    } finally {
      closeModel('add-cse')
      setIsLoading(false)
    }
  }

  return (
    <InnerBox className={'rounded-2xl flex flex-col gap-3'}>
      {defaultCseCode ? (
        <div className="flex flex-row gap-3 items-center">
          <Typography variant={'tag'}>Code CSE</Typography>
          <Badge text={`CSE : ${defaultCseCode}`} />
        </div>
      ) : (
        <>
          <Typography variant={'tag'}>Code CSE</Typography>
          <Input
            type="text"
            value={cseCode}
            label="Code Partenaire CSE"
            onChange={(e) => setCseCode(e.target.value)}
            suffix={
              <div className="flex items-center gap-2">
                {cseCode && (
                  <button
                    type="button"
                    className="bg-white-active rounded-sm w-5 h-5 flex items-center justify-center"
                    onClick={() => setCseCode(undefined)}
                  >
                    <XIcon width="15" height="15" color={tailwindColors.grey.lighter} />
                  </button>
                )}

                <Button
                  label="Appliquer"
                  variant="link"
                  type="button"
                  loading={isLoading}
                  disabled={!cseCode}
                  onClick={() => toggleModal('add-cse', cseCode)}
                />
              </div>
            }
          />
        </>
      )}

      {selectedData && (
        <Modal
          size={'medium'}
          icon={HistoryIcon}
          isLoading={isLoading}
          variant="confirmation"
          hasBorderBottom={false}
          isOpen={modalState['add-cse']}
          onCancel={() => closeModel('add-cse')}
          actionButtonsOptions={{
            cancelText: 'Annuler',
            confirmText: 'Confirmer',
            onConfirm: async () => await addCseCode(selectedData)
          }}
          title="Confirmez-vous l’ajout de ce CSE ? Une fois ajouté, il ne pourra plus être supprimé."
        >
          <div className={cn(MODAL_BODY_CLASSNAME, 'flex justify-center items-center')}>
            <Badge text={`CSE : ${selectedData}`} />
          </div>
        </Modal>
      )}
    </InnerBox>
  )
}
